import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const navSections = [
    {
        label: 'SALES',
        items: [
            { name: 'Tax Invoice',       path: 'invoices',           icon: '🧾', desc: 'GST tax invoice with CGST/SGST' },
            { name: 'Invoice',           path: 'simple-invoice',     icon: '📄', desc: 'Clean invoice with payment info' },
            { name: 'Purchase Order',    path: 'purchase-order',     icon: '📋', desc: 'Generate purchase orders' },
            { name: 'Quotes',            path: 'quotes',             icon: '📝', desc: 'Send estimates' },
            { name: 'Sales Orders',      path: 'orders-list',        icon: '🛍️', desc: 'Track orders' },
            { name: 'Payments Received', path: 'payments-received',  icon: '💳', desc: 'Record payments' },
        ]
    },
    {
        label: 'CLIENTS',
        items: [
            { name: 'Customers',         path: 'customers',          icon: '👥', desc: 'Your client list' },
        ]
    },
    {
        label: 'ANALYTICS',
        items: [
            { name: 'Reports',  path: 'reports',  icon: '📊', desc: 'Analytics & insights' },
            { name: 'Settings', path: 'settings', icon: '⚙️', desc: 'Company branding & profile' },
        ]
    },
];
const navItems = navSections.flatMap(s => s.items);

export default function SalesOrders() {
    const navigate   = useNavigate();
    const location   = useLocation();
    const { user, logout } = useAuth();

    const [branding, setBranding]   = useState({ name: user?.companyName || '', logo: null, address: user?.address || '' });
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);

    const refreshBranding = () => axios.get('/api/branding').then(r => { if (r.data.name) setBranding(r.data); }).catch(() => {});

    useEffect(() => {
        refreshBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setMobileOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Close drawer on route change (mobile)
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const handleLogout = async () => { await logout(); navigate('/login'); };
    const activeItem = navItems.find(i => location.pathname.includes(i.path));

    const SidebarContent = () => (
        <div style={{
            width: isMobile ? '270px' : (collapsed ? '68px' : '255px'),
            backgroundColor: '#0b1628',
            display: 'flex', flexDirection: 'column',
            height: '100%', flexShrink: 0,
            transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}>
            {/* Logo */}
            <div style={{ padding: '20px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: (collapsed && !isMobile) ? 'center' : 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '36px', height: '36px', flexShrink: 0, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', boxShadow: '0 4px 12px rgba(99,102,241,0.35)' }}>🛡️</div>
                        {(!collapsed || isMobile) && (
                            <div>
                                <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{branding.name}</div>
                                <div style={{ color: '#64748b', fontSize: '11px', fontWeight: '500' }}>Premium Plan</div>
                            </div>
                        )}
                    </div>
                    {isMobile && (
                        <button onClick={() => setMobileOpen(false)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', cursor: 'pointer', borderRadius: '8px', padding: '4px 10px', fontSize: '18px', fontFamily: 'Inter,sans-serif' }}>✕</button>
                    )}
                </div>
            </div>

            {/* Collapse toggle (desktop only) */}
            {!isMobile && (
                <div style={{ padding: '10px 16px', display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end' }}>
                    <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand' : 'Collapse'}>
                        {collapsed ? '▶' : '◀'}
                    </button>
                </div>
            )}

            {/* Nav Sections */}
            <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: '8px' }}>
                {navSections.map(section => (
                    <div key={section.label}>
                        {(!collapsed || isMobile) && (
                            <div style={{ padding: '14px 20px 4px', fontSize: '9px', fontWeight: '800', letterSpacing: '1.8px', color: '#334155', textTransform: 'uppercase' }}>
                                {section.label}
                            </div>
                        )}
                        {collapsed && !isMobile && (
                            <div style={{ margin: '8px 10px 2px', height: '1px', background: 'rgba(255,255,255,0.06)' }} />
                        )}
                        {section.items.map(item => {
                            const isActive = location.pathname.includes(item.path);
                            return (
                                <div
                                    key={item.path}
                                    className={`nav-item${isActive ? ' active' : ''}`}
                                    style={{ justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-start' }}
                                    onClick={() => navigate(item.path)}
                                    title={(collapsed && !isMobile) ? item.name : ''}
                                >
                                    <span className="icon">{item.icon}</span>
                                    {(!collapsed || isMobile) && <span>{item.name}</span>}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </nav>

            {/* Bottom */}
            {(!collapsed || isMobile) && (
                <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {user && (
                        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '9px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', flexShrink: 0 }}>
                                {user.email.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <div style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.companyName}</div>
                                <div style={{ color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        style={{ width: '100%', padding: '9px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', fontFamily: 'Inter,sans-serif' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                    >🚪 Sign Out</button>
                </div>
            )}
        </div>
    );

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { margin: 0; font-family: 'Inter', sans-serif; }

                .nav-item {
                    display: flex; align-items: center; gap: 12px; padding: 11px 14px;
                    margin: 3px 10px; border-radius: 10px; cursor: pointer;
                    font-size: 14px; font-weight: 500; color: #94a3b8;
                    transition: all 0.2s cubic-bezier(0.4,0,0.2,1);
                    white-space: nowrap; overflow: hidden; position: relative;
                }
                .nav-item:hover { background: rgba(255,255,255,0.07); color: #e2e8f0; }
                .nav-item.active {
                    background: linear-gradient(135deg,rgba(59,130,246,0.2),rgba(99,102,241,0.15));
                    color: #60a5fa; font-weight: 600; box-shadow: inset 3px 0 0 #3b82f6;
                }
                .nav-item .icon { font-size: 18px; flex-shrink: 0; transition: transform 0.2s; }
                .nav-item:hover .icon { transform: scale(1.15); }

                .collapse-btn {
                    background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
                    color: #94a3b8; cursor: pointer; border-radius: 8px; padding: 6px 10px;
                    font-size: 16px; transition: all 0.2s ease; font-family: 'Inter',sans-serif;
                }
                .collapse-btn:hover { background: rgba(255,255,255,0.12); color: white; }

                .top-btn {
                    display: flex; align-items: center; gap: 6px;
                    padding: 8px 14px; border-radius: 8px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #475569; font-size: 13px; font-weight: 600;
                    cursor: pointer; font-family: 'Inter',sans-serif; transition: all 0.2s ease;
                    white-space: nowrap;
                }
                .top-btn:hover { border-color: #94a3b8; background: #f8fafc; }
                .top-btn.primary { background: linear-gradient(135deg,#3b82f6,#2563eb); color: white; border-color: transparent; box-shadow: 0 4px 12px rgba(59,130,246,0.3); }
                .top-btn.primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(59,130,246,0.35); }

                .hamburger {
                    background: none; border: none; cursor: pointer; padding: 8px;
                    border-radius: 8px; font-size: 20px; color: #475569;
                    transition: background 0.2s; display: none;
                }
                .hamburger:hover { background: #f1f5f9; }

                /* Responsive table wrapper */
                .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 12px; }

                /* ── RESPONSIVE BREAKPOINTS ── */
                @media (max-width: 768px) {
                    .hamburger { display: flex !important; align-items: center; justify-content: center; }
                    .top-btn:not(.primary) { display: none !important; }
                    .breadcrumb-wrap { display: none !important; }
                    .page-content { padding: 16px !important; }
                    .top-bar { padding: 0 16px !important; }
                    .stat-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
                    .form-row { flex-direction: column !important; }
                    .form-col { width: 100% !important; }
                }
                @media (max-width: 480px) {
                    .stat-grid { grid-template-columns: 1fr !important; }
                    .top-btn.primary { padding: 8px 10px !important; font-size: 12px !important; }
                    .top-btn.primary span.btn-text { display: none; }
                }

                /* Mobile overlay */
                .mobile-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.55);
                    z-index: 40; display: none;
                    backdrop-filter: blur(2px);
                }
                .mobile-overlay.open { display: block; }

                /* Mobile drawer */
                .mobile-drawer {
                    position: fixed; top: 0; left: 0; height: 100vh;
                    z-index: 50; transform: translateX(-100%);
                    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
                    box-shadow: 4px 0 30px rgba(0,0,0,0.3);
                }
                .mobile-drawer.open { transform: translateX(0); }

                ::-webkit-scrollbar { width: 6px; height: 6px; }
                ::-webkit-scrollbar-track { background: #f1f5f9; }
                ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
            `}</style>

            {/* Mobile overlay */}
            <div className={`mobile-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />

            {/* Mobile drawer */}
            {isMobile && (
                <div className={`mobile-drawer${mobileOpen ? ' open' : ''}`}>
                    <SidebarContent />
                </div>
            )}

            <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter,sans-serif' }}>

                {/* Desktop sidebar */}
                {!isMobile && (
                    <div className="sidebar" style={{ flexShrink: 0, boxShadow: '4px 0 20px rgba(0,0,0,0.12)', zIndex: 20 }}>
                        <SidebarContent />
                    </div>
                )}

                {/* Main area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, backgroundColor: '#f8fafc' }}>

                    {/* Top Bar */}
                    <div className="top-bar" style={{
                        height: '62px', backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '0 28px', boxShadow: '0 1px 8px rgba(0,0,0,0.04)', zIndex: 10, flexShrink: 0
                    }}>
                        {/* Left */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Hamburger — mobile only */}
                            <button className="hamburger" onClick={() => setMobileOpen(true)}>☰</button>
                            <div>
                                <div style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {activeItem?.icon} {activeItem?.name || 'Invoices'}
                                </div>
                                <div className="breadcrumb-wrap" style={{ fontSize: '13px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>Dashboard</span><span>›</span><span style={{ color: '#3b82f6' }}>{activeItem?.name || 'Invoices'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button className="top-btn" onClick={() => alert('No new notifications')}>🔔 Alerts</button>
                            <button className="top-btn primary" onClick={() => navigate('invoices')}>
                                ＋ <span className="btn-text">New Invoice</span>
                            </button>
                            <div
                                onClick={handleLogout}
                                title={`Sign out (${user?.email || ''})`}
                                style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: 'white', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                            >
                                {user?.email?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                        </div>
                    </div>

                    {/* Page Content */}
                    <div className="page-content" style={{ flex: 1, overflowY: 'auto', padding: '28px' }}>
                        <Outlet context={{ branding, refreshBranding }} />
                    </div>
                </div>
            </div>
        </>
    );
}